import React from 'react';

// Class component because React error boundaries can only be implemented with
// componentDidCatch / getDerivedStateFromError — there is no functional hook
// equivalent. Wraps each route's Outlet so that a render-time crash in one
// screen (e.g. StoreSettings reading `shop.shop_name` on a null shop) shows
// a friendly fallback instead of unmounting the whole app into a blank page.
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('[ErrorBoundary] Caught render error:', error, info);
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    // Soft retry first — if the underlying state is fine, just re-rendering
    // is enough. If it isn't, the user can hit Reload manually.
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="max-w-md w-full bg-white rounded-lg shadow p-8 text-center">
          <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4c-.77-1.33-2.69-1.33-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Something went wrong</h2>
          <p className="text-gray-600 mb-6">
            This screen ran into a problem. You can try again or head back to the dashboard.
          </p>
          <div className="flex gap-3">
            <button
              onClick={this.handleReload}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Try again
            </button>
            <a
              href="/dashboard"
              className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 inline-block text-center"
            >
              Go to Dashboard
            </a>
          </div>
        </div>
      </div>
    );
  }
}
